/**
 * Keyword-based merchant categorizer. Maps common merchant fragments
 * (lowercased) to canonical category names. Lookup uses the longest
 * matching key as a substring of the input, so "starbucks coffee" still
 * resolves to "Dining" even though the entry key is just "starbucks".
 *
 * Canonical category names match a closed list — the resolver only
 * returns a categoryId when one of the user's existing categories
 * matches the canonical name (case-insensitive). It never invents.
 */

import type { Category } from '../data/types';

export type CanonicalCategory =
  | 'Groceries'
  | 'Dining'
  | 'Gas'
  | 'Transport'
  | 'Travel'
  | 'Shopping'
  | 'Entertainment'
  | 'Subscriptions'
  | 'Health'
  | 'Utilities'
  | 'Home'
  | 'Personal Care';

/**
 * Lowercase merchant fragment → canonical category. Lookup picks the
 * LONGEST matching key, so longer / more specific fragments out-rank
 * generic ones. When the same merchant could plausibly fit two
 * categories (e.g. "lowes" → Shopping or Home), we keep the more
 * specific one (Home).
 */
export const MERCHANT_MAP: Record<string, CanonicalCategory> = {
  // Groceries
  walmart: 'Groceries',
  kroger: 'Groceries',
  publix: 'Groceries',
  aldi: 'Groceries',
  costco: 'Groceries',
  samsclub: 'Groceries',
  'sams club': 'Groceries',
  wholefood: 'Groceries',
  'whole foods': 'Groceries',
  traderjoe: 'Groceries',
  'trader joe': 'Groceries',
  heb: 'Groceries',
  safeway: 'Groceries',
  meijer: 'Groceries',
  target: 'Groceries',
  'food lion': 'Groceries',
  'harris teeter': 'Groceries',
  sprouts: 'Groceries',
  'fresh market': 'Groceries',
  'giant eagle': 'Groceries',
  'stop shop': 'Groceries',
  shoprite: 'Groceries',
  'winn-dixie': 'Groceries',
  food4less: 'Groceries',
  smiths: 'Groceries',
  'fred meyer': 'Groceries',
  vons: 'Groceries',
  ralphs: 'Groceries',
  jewel: 'Groceries',
  albertsons: 'Groceries',

  // Dining
  starbucks: 'Dining',
  dunkin: 'Dining',
  mcdonald: 'Dining',
  chickfila: 'Dining',
  'chick-fil-a': 'Dining',
  chipotle: 'Dining',
  panera: 'Dining',
  'taco bell': 'Dining',
  wendy: 'Dining',
  'burger king': 'Dining',
  subway: 'Dining',
  kfc: 'Dining',
  popeye: 'Dining',
  dominos: 'Dining',
  'pizza hut': 'Dining',
  'papa john': 'Dining',
  'olive garden': 'Dining',
  applebee: 'Dining',
  chili: 'Dining',
  outback: 'Dining',
  'texas roadhouse': 'Dining',
  'cracker barrel': 'Dining',
  ihop: 'Dining',
  dennys: 'Dining',
  'waffle house': 'Dining',
  doordash: 'Dining',
  ubereats: 'Dining',
  'uber eats': 'Dining',
  grubhub: 'Dining',
  postmates: 'Dining',
  instacart: 'Dining',
  'panda express': 'Dining',
  'in-n-out': 'Dining',
  'five guys': 'Dining',
  'shake shack': 'Dining',
  sonic: 'Dining',
  arbys: 'Dining',
  'jimmy johns': 'Dining',
  'jersey mike': 'Dining',
  qdoba: 'Dining',
  moes: 'Dining',
  sweetgreen: 'Dining',
  cava: 'Dining',
  bojangles: 'Dining',

  // Gas
  shell: 'Gas',
  exxon: 'Gas',
  chevron: 'Gas',
  bp: 'Gas',
  mobil: 'Gas',
  valero: 'Gas',
  sunoco: 'Gas',
  citgo: 'Gas',
  marathon: 'Gas',
  speedway: 'Gas',
  sheetz: 'Gas',
  wawa: 'Gas',
  racetrac: 'Gas',
  qt: 'Gas',
  pilot: 'Gas',
  loves: 'Gas',
  'kwik trip': 'Gas',
  casey: 'Gas',
  '76': 'Gas',
  arco: 'Gas',
  conoco: 'Gas',
  'phillips 66': 'Gas',

  // Transport
  uber: 'Transport',
  lyft: 'Transport',
  mta: 'Transport',
  bart: 'Transport',
  septa: 'Transport',
  parking: 'Transport',
  parkmobile: 'Transport',
  spothero: 'Transport',
  metra: 'Transport',
  caltrain: 'Transport',
  marta: 'Transport',
  wmata: 'Transport',
  amtrak: 'Transport',
  greyhound: 'Transport',
  megabus: 'Transport',
  zipcar: 'Transport',
  turo: 'Transport',

  // Travel
  delta: 'Travel',
  united: 'Travel',
  'american airlines': 'Travel',
  southwest: 'Travel',
  jetblue: 'Travel',
  'alaska air': 'Travel',
  spirit: 'Travel',
  frontier: 'Travel',
  airbnb: 'Travel',
  vrbo: 'Travel',
  marriott: 'Travel',
  hilton: 'Travel',
  hyatt: 'Travel',
  ihg: 'Travel',
  'holiday inn': 'Travel',
  hampton: 'Travel',
  expedia: 'Travel',
  booking: 'Travel',
  kayak: 'Travel',
  hotwire: 'Travel',
  priceline: 'Travel',
  orbitz: 'Travel',
  'ritz carlton': 'Travel',
  courtyard: 'Travel',
  'residence inn': 'Travel',
  'embassy suites': 'Travel',

  // Shopping
  amazon: 'Shopping',
  ebay: 'Shopping',
  etsy: 'Shopping',
  'walmart.com': 'Shopping',
  bestbuy: 'Shopping',
  ikea: 'Shopping',
  wayfair: 'Shopping',
  kohls: 'Shopping',
  macys: 'Shopping',
  nordstrom: 'Shopping',
  tjmaxx: 'Shopping',
  marshalls: 'Shopping',
  ross: 'Shopping',
  oldnavy: 'Shopping',
  gap: 'Shopping',
  hm: 'Shopping',
  zara: 'Shopping',
  uniqlo: 'Shopping',
  dicks: 'Shopping',
  academy: 'Shopping',
  rei: 'Shopping',
  dillards: 'Shopping',
  jcpenney: 'Shopping',
  'forever 21': 'Shopping',
  asos: 'Shopping',
  shein: 'Shopping',

  // Entertainment
  amc: 'Entertainment',
  regal: 'Entertainment',
  cinemark: 'Entertainment',
  ticketmaster: 'Entertainment',
  stubhub: 'Entertainment',
  eventbrite: 'Entertainment',
  'vivid seats': 'Entertainment',
  'dave busters': 'Entertainment',
  bowling: 'Entertainment',
  golf: 'Entertainment',

  // Subscriptions
  netflix: 'Subscriptions',
  hulu: 'Subscriptions',
  disney: 'Subscriptions',
  hbo: 'Subscriptions',
  max: 'Subscriptions',
  paramount: 'Subscriptions',
  peacock: 'Subscriptions',
  'apple tv': 'Subscriptions',
  'prime video': 'Subscriptions',
  spotify: 'Subscriptions',
  'apple music': 'Subscriptions',
  'youtube premium': 'Subscriptions',
  audible: 'Subscriptions',
  'kindle unlimited': 'Subscriptions',
  xbox: 'Subscriptions',
  playstation: 'Subscriptions',
  nintendo: 'Subscriptions',
  steam: 'Subscriptions',
  dropbox: 'Subscriptions',
  'google one': 'Subscriptions',
  icloud: 'Subscriptions',
  onedrive: 'Subscriptions',
  adobe: 'Subscriptions',
  canva: 'Subscriptions',

  // Health
  cvs: 'Health',
  walgreens: 'Health',
  'rite aid': 'Health',
  gnc: 'Health',
  'vitamin shoppe': 'Health',
  'quest diagnostics': 'Health',
  labcorp: 'Health',

  // Utilities
  att: 'Utilities',
  verizon: 'Utilities',
  't-mobile': 'Utilities',
  sprint: 'Utilities',
  comcast: 'Utilities',
  xfinity: 'Utilities',
  spectrum: 'Utilities',
  cox: 'Utilities',
  optimum: 'Utilities',
  'frontier comm': 'Utilities',
  centurylink: 'Utilities',
  'duke energy': 'Utilities',
  'southern co': 'Utilities',
  conedison: 'Utilities',
  pge: 'Utilities',
  dominion: 'Utilities',
  nationalgrid: 'Utilities',
  'waste management': 'Utilities',
  'republic services': 'Utilities',

  // Home — placed AFTER Shopping so home depot / lowes resolve here.
  'home depot': 'Home',
  homedepot: 'Home',
  lowes: 'Home',
  'ace hardware': 'Home',
  'harbor freight': 'Home',
  menards: 'Home',
  'sherwin williams': 'Home',

  // Personal Care — placed last so ulta / sephora resolve here per spec.
  'great clips': 'Personal Care',
  supercuts: 'Personal Care',
  ulta: 'Personal Care',
  sephora: 'Personal Care',
  'planet fitness': 'Personal Care',
  'la fitness': 'Personal Care',
  equinox: 'Personal Care',
  peloton: 'Personal Care',
  'anytime fitness': 'Personal Care',
  'orange theory': 'Personal Care',
  soulcycle: 'Personal Care',
  drybar: 'Personal Care',
  'european wax': 'Personal Care',
};

/**
 * Find the longest MERCHANT_MAP key that appears as a substring of
 * the lowercased merchant string, and resolve it to a user category
 * by case-insensitive name match. Returns the matching categoryId, or
 * null if no fragment matches OR no user category matches the
 * canonical name (we never invent a category).
 */
export function suggestCategoryFromMerchant(
  merchant: string,
  userCategories: Category[],
): string | null {
  if (!merchant) return null;
  const lower = merchant.toLowerCase();
  let bestKey = '';
  let bestCanonical: CanonicalCategory | null = null;
  for (const [key, canonical] of Object.entries(MERCHANT_MAP)) {
    if (lower.includes(key) && key.length > bestKey.length) {
      bestKey = key;
      bestCanonical = canonical;
    }
  }
  if (!bestCanonical) return null;
  const target = bestCanonical.toLowerCase();
  const cat = userCategories.find((c) => c.name.toLowerCase() === target);
  return cat?.id ?? null;
}
